import { ArrowRight } from 'lucide-react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { BadgeKind, ProductCard as ProductCardType } from '@/types/storefront';
import { FavoriteButton } from './favorite-button';
import { Price } from './price';

const BADGE_TONE: Record<BadgeKind, 'discount' | 'sameDay' | 'bestseller' | 'fresh' | 'soldOut'> = {
  discount: 'discount',
  sameDay: 'sameDay',
  bestseller: 'bestseller',
  fresh: 'fresh',
  soldOut: 'soldOut',
};

const DELIVERY_HINT_TEXT: Record<ProductCardType['deliveryHint'], string> = {
  today: 'Bugün teslim edilebilir',
  tomorrow: 'Yarından itibaren',
  soldOut: 'Şu an tükendi',
};

interface ProductCardProps {
  product: ProductCardType;
  /** Yalnızca ilk ekrandaki ilk kart için: LCP görseli. */
  priority?: boolean;
  className?: string;
}

/**
 * ÜRÜN KARTI (PRODUCT-DECISIONS §3.1)
 *
 *  - Karttan doğrudan sepete ekleme YOK; karar ürün detayında verilir.
 *  - Çerçeve yok: ayrım fotoğraf oranı ve boşlukla kurulur.
 *  - Rozet en fazla iki tane; öncelik backend'de belirlenir.
 *  - Mobilde ikinci fotoğraf yatay kaydırma ile görülür (native scroll-snap,
 *    sayfa kaydırmasıyla kavga etmez), desktop'ta hover ile geçiş yapar.
 */
export const ProductCard = memo(function ProductCard({
  product,
  priority = false,
  className,
}: ProductCardProps) {
  const cover = product.images[0];
  const alternate = product.images[1];
  const soldOut = !product.inStock;

  return (
    <article className={cn('group relative', className)}>
      <Link
        to={`/urun/${product.slug}`}
        className="block focus-visible:outline-none"
        aria-labelledby={`product-${product.id}-name`}
      >
        <div
          className={cn(
            'relative aspect-4/5 overflow-hidden rounded-lg bg-surface-sunken',
            'transition-transform duration-base ease-flow active:scale-[0.985]',
            'group-focus-within:ring-2 group-focus-within:ring-leaf-700 group-focus-within:ring-offset-2',
          )}
        >
          {/* Mobil: yatay kaydırma (native snap — sayfa scroll'u ile kavga etmez) */}
          <div
            className={cn(
              'flex h-full w-full snap-x snap-mandatory overflow-x-auto',
              '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              'md:overflow-x-hidden',
            )}
          >
            {cover ? (
              <img
                src={cover.url}
                alt={cover.alt ?? product.name}
                width={cover.width ?? 1000}
                height={cover.height ?? 1250}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : 'auto'}
                decoding={priority ? 'sync' : 'async'}
                className={cn(
                  'h-full w-full flex-none snap-start object-cover',
                  'transition-transform duration-image ease-flow md:group-hover:scale-[1.04]',
                  soldOut && 'opacity-60 grayscale-[0.35]',
                )}
              />
            ) : (
              <div className="h-full w-full flex-none" aria-hidden />
            )}

            {/* İkinci kare yalnızca mobilde kaydırma içinde yer alır. */}
            {alternate ? (
              <img
                src={alternate.url}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="h-full w-full flex-none snap-start object-cover md:hidden"
              />
            ) : null}
          </div>

          {/* Desktop: hover'da alternatif görsele yumuşak geçiş */}
          {alternate ? (
            <img
              src={alternate.url}
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className={cn(
                'pointer-events-none absolute inset-0 hidden h-full w-full object-cover opacity-0',
                'transition-[opacity,transform] duration-image ease-flow',
                'md:block md:group-hover:scale-[1.04] md:group-hover:opacity-100',
              )}
            />
          ) : null}

          {/* Rozetler — fotoğraf üzerinde cam zemin, CTA rengi kullanılmaz */}
          {product.badges.length > 0 ? (
            <div className="pointer-events-none absolute left-2.5 top-2.5 flex flex-col items-start gap-1.5">
              {product.badges.map((badge) => (
                <Badge
                  key={badge.kind}
                  tone={BADGE_TONE[badge.kind]}
                  placement="onImage"
                >
                  {badge.label}
                </Badge>
              ))}
            </div>
          ) : null}

          {/* İkinci fotoğraf göstergesi — mobilde etkileşim ipucu */}
          {alternate ? (
            <div
              aria-hidden
              className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 gap-1 md:hidden"
            >
              <span className="size-1.5 rounded-pill bg-white/90" />
              <span className="size-1.5 rounded-pill bg-white/50" />
            </div>
          ) : null}
        </div>
      </Link>

      <div className="absolute right-2.5 top-2.5">
        <FavoriteButton
          slug={product.slug}
          productName={product.name}
          onImage
          size="sm"
        />
      </div>

      <div className="mt-3">
        <Link to={`/urun/${product.slug}`} className="focus-visible:outline-none">
          <h3
            id={`product-${product.id}-name`}
            className="line-clamp-2 font-serif text-base leading-snug text-ink transition-colors duration-fast group-hover:text-leaf-800"
          >
            {product.name}
          </h3>
        </Link>

        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <Price value={product.price} compareAt={product.compareAtPrice} size="sm" />
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          {/*
            Teslimat ipucu backend'de hesaplanır: ürünün kendi uygunluğu ile
            SEÇİLİ BÖLGENİN durumu birlikte değerlendirilir. Kart bu iki
            bilgiyi kendi başına birleştirmez.
          */}
          <span className="text-xs text-ink-muted">
            {DELIVERY_HINT_TEXT[product.deliveryHint]}
          </span>

          <span
            aria-hidden
            className="inline-flex items-center gap-1 text-xs text-leaf-600 transition-transform duration-fast ease-flow group-hover:translate-x-0.5"
          >
            İncele
            <ArrowRight className="size-3" />
          </span>
        </div>
      </div>
    </article>
  );
});
